import React from 'react';

/**
 * SessionConflictDialog - Modal dialog shown when user tries to login while already logged in elsewhere
 *
 * Props:
 * - isOpen: Boolean - Whether dialog is visible
 * - activeSession: Object - Information about the existing session
 *   - device: String - Device/browser info (e.g., "Chrome on Windows")
 *   - logged_in_at: String - ISO timestamp of when session started
 *   - ip_address: String - IP address of active session
 * - onConfirm: Function - Called when user confirms logout and login
 * - onCancel: Function - Called when user cancels
 */
const SessionConflictDialog = ({ isOpen, activeSession, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  // Format the login time
  const formatLoginTime = (isoString) => {
    if (!isoString) return 'Unknown time';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Unknown time';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-fade-in">
        {/* Icon */}
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-yellow-100 rounded-full">
          <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>

        {/* Title */}
        <h2 className="text-xl font-bold text-gray-900 text-center mb-2">
          Active Session Detected
        </h2>

        {/* Message */}
        <p className="text-gray-600 text-center mb-6">
          You are already logged in on another device. Do you want to logout from that device and login here?
        </p>

        {/* Session Info Card */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Current Active Session:</h3>

          <div className="space-y-2">
            {/* Device */}
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-gray-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <div className="flex-1">
                <p className="text-xs text-gray-500">Device</p>
                <p className="text-sm font-medium text-gray-900">{activeSession?.device || 'Unknown Device'}</p>
              </div>
            </div>

            {/* Login Time */}
            {activeSession?.logged_in_at && (
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-gray-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Logged in at</p>
                  <p className="text-sm font-medium text-gray-900">{formatLoginTime(activeSession.logged_in_at)}</p>
                </div>
              </div>
            )}

            {/* IP Address (optional, can be hidden for privacy) */}
            {activeSession?.ip_address && (
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-gray-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">IP Address</p>
                  <p className="text-sm font-medium text-gray-900">{activeSession.ip_address}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
          >
            Logout & Login Here
          </button>
        </div>

        {/* Info text */}
        <p className="text-xs text-gray-500 text-center mt-4">
          Logging in here will end your session on the other device.
        </p>
      </div>
    </div>
  );
};

export default SessionConflictDialog;
